import { Row } from "@tanstack/react-table";
import classNames from "classnames";
import { Tooltip } from "PFComponents/tooltip";
import { Typography } from "PFComponents/typography";
import { useIsElementVisible } from "PFCore/hooks/use_is_element_visible";
import { ReactNode, RefObject } from "react";

import { InteractiveTableCell } from "../interactive/interactive_table_cell";
import { RowSelectionConditions } from "../selection";
import { TableData } from "../table";
import css from "../table.module.scss";
import { OnRowClickFn } from "../table.types";
import { TableCell } from "./table_cell";

type TableRowProps<TABLE_DATA extends TableData> = {
  row: Row<TABLE_DATA>;
  rowIndex: number;
  onRowClick?: OnRowClickFn<TABLE_DATA>;
  compact?: boolean;
  topAlignContent?: boolean;
  noHover?: boolean;
  displayGrid?: boolean;
  lastRowRef?: RefObject<HTMLTableRowElement | null>;
  renderExpandedRow?: (row: TABLE_DATA) => ReactNode;
  rowSelectionConditions?: RowSelectionConditions<TABLE_DATA>;
  scrollableContainerRef?: RefObject<HTMLElement | null>;
  hideDistantRows?: boolean;
  isInteractive?: boolean;
};

export const TableRow = <TABLE_DATA extends TableData>({
  row,
  rowIndex,
  onRowClick,
  compact,
  topAlignContent,
  noHover,
  lastRowRef,
  renderExpandedRow,
  rowSelectionConditions,
  displayGrid,
  scrollableContainerRef,
  hideDistantRows,
  isInteractive
}: TableRowProps<TABLE_DATA>) => {
  const { disabled, message } = row.original;
  const isExpandable = rowSelectionConditions?.isRowExpandable?.(row);

  const { isVisible, elementRef } = useIsElementVisible({
    scrollableContainerRef,
    active: hideDistantRows ?? false
  });

  const CellComponent = isInteractive ? InteractiveTableCell : TableCell;

  const handleRowClick = () => {
    if (onRowClick) {
      onRowClick(row.original);
    }
  };

  return (
    <>
      <Tooltip
        content={
          <Typography variant="bodyRegular" tag="span">
            {message}
          </Typography>
        }
        disabled={!message}
        placement="top-start"
        triggerRef={lastRowRef}
      >
        {({ ref, getReferenceProps }) => (
          <tr
            ref={(element) => {
              elementRef.current = element;
              if (typeof ref === "function") {
                ref(element);
              } else if (ref) {
                ref.current = element;
              }
            }}
            {...getReferenceProps({
              className: classNames(css.row, css.rowBody, {
                [css.noHover]: noHover,
                [css.topAlignContent]: topAlignContent
              }),
              style: { cursor: onRowClick ? "pointer" : "default" },
              onClick: handleRowClick
            })}
          >
            {isVisible || !hideDistantRows
              ? row
                  .getVisibleCells()
                  .filter((cell) => {
                    // Skip cells absorbed by a preceding rowSpan merge.
                    const { isRowSpanHidden } = cell.column.columnDef.meta || {};
                    return !isRowSpanHidden?.(rowIndex);
                  })
                  .map((cell, visibleIndex, cells) => (
                    <CellComponent
                      key={cell.id}
                      cell={cell}
                      compact={compact}
                      displayGrid={displayGrid}
                      disabled={disabled}
                      hideDistantRows={hideDistantRows}
                      rowIndex={rowIndex}
                      colIndex={cell.column.getIndex()}
                      isLastColumn={visibleIndex === cells.length - 1}
                    />
                  ))
              : row
                  .getVisibleCells()
                  .filter((cell) => {
                    const { isRowSpanHidden } = cell.column.columnDef.meta || {};
                    return !isRowSpanHidden?.(rowIndex);
                  })
                  .map((cell) => {
                    const { getRowSpan } = cell.column.columnDef.meta || {};
                    const rowSpan = getRowSpan ? getRowSpan(rowIndex) : undefined;
                    return <td key={cell.id} rowSpan={rowSpan} style={{ width: cell.column.getSize() }} />;
                  })}
          </tr>
        )}
      </Tooltip>
      {isExpandable && row.getIsExpanded() && (
        <tr className={css.expandedContentRow}>
          <td className={css.expandedContent} colSpan={row.getAllCells().length}>
            {renderExpandedRow?.(row.original)}
          </td>
        </tr>
      )}
    </>
  );
};
